import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useState } from 'react';

import {
  type AudienceActivationType,
  type AudienceType,
  mutateAudienceActivate,
  mutateAudienceDeactivate,
  mutateAudienceDelete,
  useAudienceList,
} from '@/api/audience';
import {
  InstantSearch,
  InstantSearchSortOrder,
  type QueryState,
  type SolQueryParams,
} from '@/components/InstantSearch';
import { getSolQueryParamsFromQueryState } from '@/components/InstantSearch/util/search-util';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import useErrorDisplay from '@/error/useErrorDisplay';
import useDisplayNotificationOnLoad from '@/hooks/useDisplayNotificationOnLoad';
import useEffectOnceWhenDepsExist from '@/hooks/useEffectOnceWhenDepsExist';
import useLocationState from '@/hooks/useLocationState';
import { useNotification } from '@/providers/Notification';

import AudiencesListHeader from './AudiencesListHeader';
import AudiencesListTable from './AudiencesListTable';

const DEFAULT_QUERY_STATE: Partial<QueryState> = {
  sortBy: 'created_at',
  sortOrder: InstantSearchSortOrder.DESC,
};

const AudiencesList = () => {
  const [tableParams, setTableParams] = useState<SolQueryParams>();
  const { successMessage, optimisticUpdate } = useLocationState<{
    successMessage: string;
    optimisticUpdate: AudienceType;
  }>();
  const {
    audienceListData,
    totalResults,
    isLoading,
    error,
    mutate: mutateListData,
  } = useAudienceList(tableParams);
  const displayError = useErrorDisplay();
  const { pushNotification, removeAllNotifications } = useNotification();

  useDisplayNotificationOnLoad({ type: 'success', message: successMessage });
  useEffectOnceWhenDepsExist(() => {
    if (audienceListData && totalResults) {
      mutateListData({
        audiences: {
          get: {
            edges: audienceListData?.map((audience) =>
              audience.id === optimisticUpdate.id
                ? { node: { ...audience, ...optimisticUpdate } }
                : { node: audience },
            ),
            totalEdges: totalResults,
          },
        },
      });
    }
  }, [optimisticUpdate, audienceListData, mutateListData]);

  const sendErrorNotification = (message: string) => {
    pushNotification({ type: 'error', message });
  };

  const removeAudienceFromList = (audienceId: string) => {
    return audienceListData?.filter((audience) => audience.id !== audienceId);
  };

  const handleQueryStateChange = useCallback(
    (queryState: Required<QueryState>) => {
      const tableParams = getSolQueryParamsFromQueryState(queryState);
      setTableParams(tableParams);
    },
    [setTableParams],
  );

  const handleAudienceDelete = async (audienceId: string) => {
    try {
      // Optimistic Update on the pagination data
      const filteredList = removeAudienceFromList(audienceId);
      const success = await mutateAudienceDelete(audienceId);

      if (filteredList && totalResults) {
        mutateListData(
          {
            audiences: {
              get: {
                edges: filteredList?.map((audience) => ({ node: audience })),
                totalEdges: totalResults,
              },
            },
          },
          { revalidate: false },
        );
      }

      if (!success) {
        sendErrorNotification(t`Failed to delete the audience. Please try again.`);
        return;
      }
    } catch (e) {
      displayError({ error: e });
    }
  };

  const handleAudienceActivate = async (audienceId: string, activation: AudienceActivationType) => {
    try {
      removeAllNotifications();
      const success = await mutateAudienceActivate(audienceId, activation.provider.id);

      if (success) {
        pushNotification({
          type: 'success',
          message: t`Audience activation to ${activation.provider.name} was successful. Please wait up to 24 hours for the audience to show in their platform.`,
        });
        mutateListData(
          {
            audiences: {
              get: {
                edges:
                  audienceListData?.map((audience) => ({
                    node:
                      audience.id === audienceId
                        ? ({
                            ...audience,
                            activations: (audience.activations ?? []).concat(activation),
                          } satisfies AudienceType)
                        : audience,
                  })) ?? [],
                totalEdges: totalResults ?? 0,
              },
            },
          },
          {
            revalidate: false,
          },
        );
      } else {
        sendErrorNotification(t`Failed to activate the audience. Please try again.`);
        return;
      }
    } catch (e) {
      displayError({ error: e });
    }
  };

  const handleAudienceDeactivate = async (
    audienceId: string,
    activation: AudienceActivationType,
  ) => {
    try {
      removeAllNotifications();
      const success = await mutateAudienceDeactivate(audienceId, activation.provider.id);

      if (success) {
        pushNotification({
          type: 'success',
          message: t`Audience deactivation from ${activation.provider.name} was successful. Please wait up to 24 hours for the audience to be removed from their platform.`,
        });
        mutateListData(
          {
            audiences: {
              get: {
                edges:
                  audienceListData?.map((audience) => ({
                    node:
                      audience.id === audienceId
                        ? ({
                            ...audience,
                            activations: (audience.activations ?? []).filter(
                              (act) => act.provider.id !== activation.provider.id,
                            ),
                          } satisfies AudienceType)
                        : audience,
                  })) ?? [],
                totalEdges: totalResults ?? 0,
              },
            },
          },
          {
            revalidate: false,
          },
        );
      } else {
        sendErrorNotification(t`Failed to deactivate the audience. Please try again.`);
        return;
      }
    } catch (e) {
      displayError({ error: e });
    }
  };

  return (
    <Page title={t`Configure - Audiences`} exampleAIQuestions={exampleAIQuestions('audience')}>
      <InstantSearch
        prefix="a"
        defaultQueryState={DEFAULT_QUERY_STATE}
        onQueryStateChange={handleQueryStateChange}
      >
        <div className="flex flex-col gap-6">
          <AudiencesListHeader />
          <Panel title={<Trans>Audiences</Trans>} data-testid="audiences-table-panel" isFullHeight>
            <AudiencesListTable
              data={audienceListData}
              totalResults={totalResults}
              isLoading={isLoading}
              error={error}
              onAudienceDelete={handleAudienceDelete}
              onAudienceActivate={handleAudienceActivate}
              onAudienceDeactivate={handleAudienceDeactivate}
            />
          </Panel>
        </div>
      </InstantSearch>
    </Page>
  );
};

export default AudiencesList;
